import { useEffect, useState, useRef, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { ImageViewer } from "@/features/accounts/ocr/components/images-viewer"
import { ImageFile } from "@/features/accounts/ocr/hooks/use-images-viewer-states"
import { OcrResultsTable } from "@/features/accounts/ocr/components/results-table"
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useProcessStatus } from "@/features/accounts/ocr/hooks/use-process-status";

interface Props {
  onTapCloseModal: () => void
  onCompleteModal: () => void
  viewMode: "check" | "preview"
  tradeId?: string
  processId?: string
}


export const CheckOcrTrades = (props: Props) => {
  const { onTapCloseModal, onCompleteModal, viewMode, tradeId, processId } = props
  const [selectedImageFileName, setSelectedImageFileName] = useState<string>("")
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([])
  const editOcrRef = useRef<HTMLElement | null>(null)

  const { selectedAccount } = useAccountGroupsContext();
  const { putProcessStatus } = useProcessStatus();

  // imageViewerで画像リスト取得時に呼ばれる
  const handleImagesLoad = (images: ImageFile[]) => {
    setImageFiles(images)
    setSelectedImageFileName((prev) => {
      if (prev !== "") return prev
      // 送られてきた画像の最初を選択(checkモード時は未完了の最初の画像)
      const firstImage = images.find(img => !img.isCompleted) || images[0]
      return firstImage ? firstImage.filename : ""
      
    })
  }

  // 画像の処理完了時に呼ばれる
  const handleProcessedComplete = async (filename: string) => {
    if (filename !== "") {
      const updated = imageFiles.map((img) => (
        img.filename === filename ? { ...img, isCompleted: true } : img
      ))
      setImageFiles(updated)

      if (selectedAccount) {
        const draftKey = `ocr_draft_${selectedAccount.mlinkAccountId}_${selectedAccount.ocrProcessId}`;
        const draftKey_image = `${draftKey}_images`;
        localStorage.setItem(draftKey_image, JSON.stringify(updated));
        // 全て完了ならAPI呼び出し
        if (updated.every(img => img.isCompleted)) {
          try {
            await putProcessStatus(selectedAccount.ocrProcessId);
            localStorage.removeItem(draftKey);
            localStorage.removeItem(draftKey_image);
            onCompleteModal();
          } catch (e) {
            console.log("putProcessStatus", e);
          }
        }
      }

      // 完了画像を選択中なら未完了画像の最初に選択を移す
      if (selectedImageFileName === filename) {
        const nextIncomplete = updated.find(img => !img.isCompleted)
        if (nextIncomplete) {
          setSelectedImageFileName(nextIncomplete.filename)
        }
      }
    }
    else {
      // filenameが空文字の場合は、画像の処理完了後にAPIエラーなどで画像の状態が更新できなかった場合を想定
      // その場合はモーダルを閉じる
      onTapCloseModal();
    }
  }

  useEffect(() => {
    if (viewMode === "check" && selectedAccount && imageFiles.length > 0) {
      localStorage.setItem(
        `ocr_draft_${selectedAccount.mlinkAccountId}_${selectedAccount.ocrProcessId}_images`, JSON.stringify(imageFiles));
    }
  }, [imageFiles])

  // OCRモーダルのフォーカストラップ
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    if (!editOcrRef.current) return;

    const focusableElements = Array.from(
      editOcrRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      if (["SECTION", "DIV", "ASIDE"].includes(el.tagName)) return false;
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
    if (focusableElements.length === 0) return;

    e.preventDefault();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = e.shiftKey ? focusableElements.length - 1 : 0;
    } else if (e.shiftKey) {
      nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
    }
    focusableElements[nextIndex].focus();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // 現在選択中の画像オブジェクトを取得
  const selectedImage = imageFiles.find((img) => img.filename === selectedImageFileName)

  return (
    <aside id="editOcr" className="edit_ocr active" ref={editOcrRef}>
    <AnimatePresence>
      <motion.div
        key="modal"
        id="ocrContainer"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
      >
          <ImageViewer
            imageFiles={imageFiles}
            selectedImage={selectedImage}
            viewMode={viewMode}
            tradeId={viewMode === "preview" ? tradeId : undefined}
            processId={viewMode === "preview" ? processId : undefined}
            onImageSelect={setSelectedImageFileName}
            onImagesLoad={handleImagesLoad}
            onProcessedComplete={handleProcessedComplete}
          />
          {viewMode === "check" && (
            <OcrResultsTable
              imageId={selectedImageFileName}
              imageOriginName={selectedImage?.originFilename || ""}
              onProcessedComplete={handleProcessedComplete}
              isCompleted={selectedImage?.isCompleted ?? false}
            />
          )}
        <BaseRoundButton
          className="close-ocr"
          onClick={() => {onTapCloseModal();}}
          loadingNegative={true}
        />
      </motion.div>
    </AnimatePresence>
    </aside>
  )
}
